import { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DownloadIcon } from './icons';
import { ConfirmationDialog } from './confirmation-dialog';
import { dynamicFileSize } from '../lib/dynamic-file-size';
import { openDocumentPrintWindow } from '../lib/open-document-print-window';

export interface DocumentItemData {
  id: string;
  title: string;
  description?: string | null;
  tags?: string[];
  /** Present for file items */
  downloadUrl?: string | null;
  /** Present for file items */
  fileName?: string | null;
  /** Present for file items */
  fileSize?: number | null;
  /** Present for document items */
  documentContent?: unknown;
}

interface DocumentItemDetailDialogProps {
  open: boolean;
  item: DocumentItemData;
  availableTags?: { name: string; color?: string | null }[];
  onClose: () => void;
  onSave: (title: string, description: string, tags: string[]) => Promise<void>;
  onDelete: () => Promise<void>;
  saving?: boolean;
  deleting?: boolean;
  disabled?: boolean;
}

export function DocumentItemDetailDialog({
  open,
  item,
  availableTags = [],
  onClose,
  onSave,
  onDelete,
  saving,
  deleting,
  disabled,
}: DocumentItemDetailDialogProps) {
  const [title, setTitle] = useState(item.title);
  const [description, setDescription] = useState(item.description ?? '');
  const [tags, setTags] = useState(item.tags ?? []);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const tagColorByName = useMemo(
    () =>
      new Map(
        availableTags.map((tag) => [
          tag.name.toLocaleLowerCase(),
          tag.color || undefined,
        ]),
      ),
    [availableTags],
  );

  useEffect(() => {
    setTitle(item.title);
    setDescription(item.description ?? '');
    setTags(item.tags ?? []);
  }, [item]);

  const handleSave = async () => {
    await onSave(title, description, tags);
    onClose();
  };

  const handleDelete = async () => {
    await onDelete();
    setConfirmDelete(false);
    onClose();
  };

  const isLoading = saving || deleting;
  const documentAttachmentSize = useMemo(() => getDocumentAttachmentSize(item.documentContent), [item.documentContent]);
  const displayedSize = item.fileSize ?? documentAttachmentSize;

  return (
    <>
      <Dialog open={open && !confirmDelete} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Detail dokumentu</DialogTitle>
        <DialogContent>
          <TextField
            label="Názov"
            fullWidth
            margin="normal"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={isLoading || disabled}
          />
          <TextField
            label="Popis"
            fullWidth
            margin="normal"
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLoading || disabled}
          />
          {item.tags && (
            <Autocomplete
              multiple
              freeSolo
              options={availableTags.map((tag) => tag.name)}
              value={tags}
              onChange={(_event, value) => {
                const nextTags = Array.from(
                  new Set(value.map((tag) => tag.trim()).filter((tag) => !!tag && tag.length <= 20)),
                );
                setTags(nextTags);
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                    label={option}
                    sx={{ backgroundColor: tagColorByName.get(option.toLocaleLowerCase()) }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} margin="normal" label="Tagy" helperText="Maximálne 20 znakov na tag." />
              )}
              disabled={isLoading || disabled}
            />
          )}
          {(item.downloadUrl || item.documentContent) && (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
              {item.downloadUrl ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  component="a"
                  href={item.downloadUrl}
                  download={item.fileName ?? true}
                  target="_blank"
                  rel="noreferrer"
                >
                  Stiahnuť
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={() => openDocumentPrintWindow(item.title, item.documentContent)}
                >
                  Stiahnuť
                </Button>
              )}
              {displayedSize != null && (
                <Typography variant="body2" color="text.secondary">
                  {dynamicFileSize(displayedSize)}
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Button color="error" onClick={() => setConfirmDelete(true)} disabled={isLoading || disabled}>
            Zmazať
          </Button>
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={isLoading}>
              Zavrieť
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={isLoading || !title || disabled}>
              {saving ? 'Ukladá sa...' : 'Uložiť'}
            </Button>
          </Stack>
        </DialogActions>
        {isLoading && <LinearProgress />}
      </Dialog>

      {confirmDelete && (
        <ConfirmationDialog
          open={confirmDelete}
          title="Zmazať dokument"
          message="Naozaj chcete zmazať tento dokument?"
          loading={deleting}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}

function getDocumentAttachmentSize(content?: unknown): number | null {
  if (!content || typeof content !== 'object') {
    return null;
  }

  const blocks = (content as { blocks?: unknown }).blocks;
  if (!Array.isArray(blocks)) {
    return null;
  }

  let total = 0;
  let foundAny = false;

  for (const block of blocks) {
    if (!block || typeof block !== 'object') {
      continue;
    }

    const typedBlock = block as { type?: unknown; data?: unknown };
    if (typedBlock.type !== 'image' && typedBlock.type !== 'attaches') {
      continue;
    }

    if (!typedBlock.data || typeof typedBlock.data !== 'object') {
      continue;
    }

    const fileData = (typedBlock.data as { file?: unknown }).file;
    if (!fileData || typeof fileData !== 'object') {
      continue;
    }

    const fileSize = (fileData as { fileSize?: unknown; size?: unknown }).fileSize;
    const fallbackSize = (fileData as { fileSize?: unknown; size?: unknown }).size;
    const numericSize = typeof fileSize === 'number' ? fileSize : typeof fallbackSize === 'number' ? fallbackSize : null;

    if (numericSize != null) {
      total += numericSize;
      foundAny = true;
    }
  }

  return foundAny ? total : null;
}
